/**
 * Guest "version genome": first-contact mapping from answers → unique PWA configuration.
 * The PWA adapts to the person; the person is never forced into a fixed shell.
 */

import { hasMedicalCaution, normalizeMedicalFlags } from './quickhook-medical.js';
import { normalizeGoals, pickPrimaryGoal } from './quickhook-goals.js';
import { normalizeFeelings, pickPrimaryFeeling } from './quickhook-feeling.js';

const TOOL_CATALOG = [
  'recommendations',
  'nutrition',
  'metabolism',
  'neuroReset',
  'progress',
  'triage',
  'specialists',
  'community',
  'labs',
  'alerts',
  'emotionalRegulation',
];

function clampAge(age) {
  const n = Number(age);
  if (!Number.isFinite(n)) return 40;
  return Math.max(14, Math.min(100, Math.round(n)));
}

function fingerprint({ goals, feelings, age, gender, medicalFlags }) {
  const medical = normalizeMedicalFlags(medicalFlags).slice().sort().join('+');
  const g = normalizeGoals(goals).slice().sort().join('+');
  const f = normalizeFeelings(feelings).slice().sort().join('+');
  const raw = `${g}|${f}|${age}|${gender || 'x'}|${medical}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash * 31 + raw.charCodeAt(i)) >>> 0;
  }
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'AF-';
  let n = hash || 1;
  for (let i = 0; i < 5; i += 1) {
    code += alphabet[n % alphabet.length];
    n = Math.floor(n / alphabet.length) || hash + i + 7;
  }
  return code;
}

function profileFromGoal(g) {
  if (g === 'gain_muscle') return 'athlete';
  if (g === 'lose_weight' || g === 'eat_better') return 'metabolic';
  if (g === 'mental_wellbeing' || g === 'sleep_better') return 'mental';
  if (g === 'move_better') return 'mobility';
  if (g === 'more_energy') return 'vitality';
  return 'longevity';
}

/**
 * @returns {{
 *  profileKey: string,
 *  toolIds: string[],
 *  genomeId: string,
 *  uniquenessScore: number
 * }}
 */
export function resolveGuestVersion({
  goal,
  goals,
  feeling,
  feelings,
  age,
  gender,
  medicalFlags,
}) {
  const safeAge = clampAge(age);
  const goalList = normalizeGoals(goals || goal);
  const feelingList = normalizeFeelings(feelings || feeling);
  const g = pickPrimaryGoal(goalList);
  const f = pickPrimaryFeeling(feelingList);
  const medical = normalizeMedicalFlags(medicalFlags);
  const medicalHard = hasMedicalCaution(medical);
  const familyAware = medical.includes('family_history') && !medical.includes('none');

  let profileKey = profileFromGoal(g);
  // Secondary goals can tilt the profile without wiping primary.
  if (goalList.includes('mental_wellbeing') || goalList.includes('sleep_better')) {
    if (profileKey === 'athlete') profileKey = 'performance';
    else if (profileKey !== 'caution') profileKey = profileKey === 'metabolic' ? 'metabolic' : 'mental';
  }
  if (feelingList.includes('stressed') || feelingList.includes('anxious') || g === 'mental_wellbeing') {
    profileKey = profileKey === 'athlete' ? 'performance' : 'mental';
  }
  if (
    medicalHard ||
    feelingList.includes('sore') ||
    (safeAge >= 65 && (feelingList.includes('tired') || feelingList.includes('sore')))
  ) {
    profileKey = 'caution';
  } else if (feelingList.includes('tired') && profileKey === 'athlete') {
    profileKey = 'performance';
  } else if (safeAge >= 60 && profileKey === 'athlete') {
    profileKey = 'performance';
  } else if (safeAge >= 60 && (profileKey === 'metabolic' || profileKey === 'vitality')) {
    profileKey = 'longevity';
  }

  const baseByProfile = {
    athlete: ['recommendations', 'metabolism', 'nutrition', 'progress', 'emotionalRegulation', 'neuroReset'],
    performance: ['recommendations', 'neuroReset', 'metabolism', 'progress', 'nutrition', 'alerts'],
    metabolic: ['nutrition', 'metabolism', 'recommendations', 'progress', 'labs', 'neuroReset'],
    mental: ['neuroReset', 'emotionalRegulation', 'triage', 'recommendations', 'progress', 'community'],
    mobility: ['recommendations', 'progress', 'triage', 'neuroReset', 'specialists', 'nutrition'],
    longevity: ['recommendations', 'progress', 'nutrition', 'labs', 'alerts', 'specialists'],
    vitality: ['recommendations', 'nutrition', 'neuroReset', 'progress', 'metabolism', 'alerts'],
    caution: ['triage', 'specialists', 'neuroReset', 'alerts', 'recommendations', 'progress'],
  };

  let toolIds = [...(baseByProfile[profileKey] || baseByProfile.longevity)];

  if (goalList.includes('eat_better') && !toolIds.includes('nutrition')) {
    toolIds = ['nutrition', ...toolIds].slice(0, 6);
  }
  if (goalList.includes('sleep_better') && !toolIds.includes('neuroReset')) {
    toolIds = ['neuroReset', ...toolIds.filter((id) => id !== 'neuroReset')].slice(0, 6);
  }
  if (
    (feelingList.includes('good') || feelingList.includes('energetic') || feelingList.includes('motivated')) &&
    !toolIds.includes('recommendations') &&
    profileKey !== 'caution'
  ) {
    toolIds = ['recommendations', ...toolIds].slice(0, 6);
  }
  if (
    (feelingList.includes('stressed') || feelingList.includes('anxious')) &&
    !toolIds.includes('neuroReset')
  ) {
    toolIds = ['neuroReset', ...toolIds.filter((id) => id !== 'neuroReset')].slice(0, 6);
  }
  if ((safeAge >= 55 || medicalHard || familyAware) && !toolIds.includes('specialists')) {
    toolIds = [...toolIds.slice(0, 5), 'specialists'];
  }
  if ((medicalHard || familyAware) && !toolIds.includes('labs')) {
    toolIds = [...toolIds.filter((id) => id !== 'labs'), 'labs'].slice(0, 6);
  }
  if (medicalHard && !toolIds.includes('triage')) {
    toolIds = ['triage', ...toolIds.filter((id) => id !== 'triage')].slice(0, 6);
  }

  const selected = new Set(toolIds);
  toolIds = TOOL_CATALOG.filter((id) => selected.has(id)).slice(0, 6);

  const genomeId = fingerprint({
    goals: goalList,
    feelings: feelingList,
    age: safeAge,
    gender,
    medicalFlags: medical,
  });
  const uniquenessScore = 70 + (genomeId.charCodeAt(3) % 27);

  return {
    profileKey,
    toolIds,
    genomeId,
    uniquenessScore,
    age: safeAge,
    goal: g,
    goals: goalList,
    feeling: f,
    feelings: feelingList,
    medicalFlags: medical,
  };
}

export { TOOL_CATALOG };
